import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import { extendSession } from '../services/api';

export const AuthContext = createContext();

// Matches Home.jsx's own inactivity-tracking event list (scheduleInactivityReset)
// - the same signals count as "the user is here" for both purposes.
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];

// How often to check whether the session needs extending. Deliberately much
// shorter than the token's own TTL (900s/15min server-side default) so a
// user who's genuinely active gets renewed well before expiry, not right at
// the edge of a network hiccup.
const SESSION_CHECK_INTERVAL_MS = 30_000;

// Extend once fewer than this many seconds remain on the current token -
// leaves comfortable headroom below the 30s check interval and normal
// request latency.
const SESSION_EXTEND_BUFFER_SECONDS = 180;

// The actual promise being implemented ("at least 15 minutes of
// inactivity", not a fixed time-since-login) - a session keeps extending
// as long as any activity happened within this window, independent of
// whatever TTL the server happens to issue tokens with.
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;

function normalizeUser(userData) {
  if (!userData) {
    return null;
  }

  return {
    ...userData,
    id: userData.id || userData.sub || userData.email || null,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState(null);
  const [loading, setLoading] = useState(true);

  // Refs (not state) so the activity listeners and the periodic extend
  // check below always read the current token/expiry without needing to be
  // torn down and re-attached on every extend - only `login`/`logout`
  // trigger a re-render via the state setters above; these refs just mirror
  // that state for synchronous reads inside event handlers/intervals.
  const tokenRef = useRef(null);
  const tokenExpiresAtRef = useRef(null);
  const lastActivityAtRef = useRef(Date.now());

  // Check if user/session data is persisted in localStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(normalizeUser(JSON.parse(storedUser)));
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        localStorage.removeItem('user');
      }
    }
    const storedToken = localStorage.getItem('authToken');
    const storedExpiresAt = localStorage.getItem('authTokenExpiresAt');
    if (storedToken) {
      setToken(storedToken);
      setTokenExpiresAt(storedExpiresAt ? Number(storedExpiresAt) : null);
      tokenRef.current = storedToken;
      tokenExpiresAtRef.current = storedExpiresAt ? Number(storedExpiresAt) : null;
    }
    setLoading(false);
  }, []);

  // Stores just the token/expiresAt half of a session (both state and
  // localStorage) - shared by `login` (which also sets `user`) and the
  // silent inactivity-based extension below (which must NOT touch `user`,
  // since nothing about the account changed, only the token's TTL).
  const applySession = (session) => {
    if (!session || !session.token) return;
    setToken(session.token);
    setTokenExpiresAt(session.expiresAt || null);
    tokenRef.current = session.token;
    tokenExpiresAtRef.current = session.expiresAt || null;
    localStorage.setItem('authToken', session.token);
    if (session.expiresAt) {
      localStorage.setItem('authTokenExpiresAt', String(session.expiresAt));
    } else {
      localStorage.removeItem('authTokenExpiresAt');
    }
  };

  // `session` is the { token, expiresAt } pair issued by the server's
  // /api/auth/refresh (server/index.js) after verifying the Google ID
  // token - this is the JWT that gets attached as an Authorization: Bearer
  // header by src/services/api.js for any authenticated backend call,
  // including the FR-8 Quiz Service's "layer 1" login-JWT verification
  // (docs/DECISIONS.md, 2026-07-28 "FR-8 Quiz Portal: auth architecture...").
  const login = (userData, session) => {
    const normalizedUser = normalizeUser(userData);
    setUser(normalizedUser);
    localStorage.setItem('user', JSON.stringify(normalizedUser));
    lastActivityAtRef.current = Date.now();
    applySession(session);
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setTokenExpiresAt(null);
    tokenRef.current = null;
    tokenExpiresAtRef.current = null;
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
    localStorage.removeItem('authTokenExpiresAt');
  };

  // True inactivity-based session length (docs/DECISIONS.md, "Login
  // session: true inactivity-based extension") - the server-issued token
  // has a fixed TTL (900s/15min by default), but as long as the user is
  // actually doing something, this silently exchanges it for a fresh one
  // before it expires, so an active session never gets cut off. If the user
  // genuinely stops interacting, no more extend calls go out and the
  // current token simply expires at its normal TTL after that last real
  // activity - the session ends only after real idle time, not a fixed
  // clock from login.
  const recordActivity = useCallback(() => {
    lastActivityAtRef.current = Date.now();
  }, []);

  const hasToken = Boolean(token);

  useEffect(() => {
    // Depends on hasToken (login/logout transitions), not `token` itself -
    // every silent extend also changes `token`, and re-attaching listeners/
    // restarting this interval on each one would be pure churn.
    if (!hasToken) return undefined;

    ACTIVITY_EVENTS.forEach((eventName) => window.addEventListener(eventName, recordActivity, { passive: true }));

    const intervalId = window.setInterval(() => {
      const currentToken = tokenRef.current;
      const expiresAt = tokenExpiresAtRef.current;
      if (!currentToken || !expiresAt) return;

      const secondsRemaining = expiresAt - Math.floor(Date.now() / 1000);
      const idleForMs = Date.now() - lastActivityAtRef.current;
      const withinIdleWindow = idleForMs < IDLE_TIMEOUT_MS;

      if (secondsRemaining <= SESSION_EXTEND_BUFFER_SECONDS && withinIdleWindow) {
        extendSession(currentToken)
          .then((session) => applySession(session))
          .catch((error) => {
            // A transient network blip shouldn't force a logout here - the
            // next tick retries, and if the token is genuinely no longer
            // valid, the next real API call's 401 handling
            // (src/services/api.js's registerSessionExpiredHandler) takes
            // care of it.
            console.warn('Session extend failed:', error);
          });
      }
    }, SESSION_CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => window.removeEventListener(eventName, recordActivity));
      window.clearInterval(intervalId);
    };
  }, [hasToken, recordActivity]);

  return (
    <AuthContext.Provider value={{ user, token, tokenExpiresAt, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
